import { ChevronDown } from "lucide-react";
import { useLocale, useTranslations } from "use-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing, type AppLocale } from "@/i18n/routing";
import { LOCALE_LABELS } from "@/i18n/locale-meta";

/**
 * Interface-language dropdown (auth pages, admin header). Switching keeps
 * the visitor on the same page — only the URL's locale prefix changes.
 *
 * The closed control shows the short code ("UZ") on phones and the full
 * name ("O'zbekcha") from `sm` up, so the landing header's Login and
 * Register buttons still fit in one row on a 360 px screen. The native
 * <select> sits invisibly on top, so the opened list, keyboard use and
 * screen readers work exactly as before.
 */
export function LocaleSwitcher() {
  const t = useTranslations("common");
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();

  return (
    <label className="relative flex items-center gap-1 rounded-lg border border-border bg-card px-2 py-1.5 text-sm text-foreground transition-colors hover:border-primary/40 focus-within:ring-2 focus-within:ring-ring">
      <span className="sr-only">{t("language.switchTo")}</span>
      <span aria-hidden="true" className="font-medium uppercase sm:hidden">
        {locale}
      </span>
      <span aria-hidden="true" className="hidden sm:inline">
        {LOCALE_LABELS[locale as AppLocale] ?? locale}
      </span>
      <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
      <select
        value={locale}
        onChange={(event) => router.replace(pathname, { locale: event.target.value as AppLocale })}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {routing.locales.map((code) => (
          <option key={code} value={code}>
            {LOCALE_LABELS[code] ?? code}
          </option>
        ))}
      </select>
    </label>
  );
}
